import type { LocationQuery, RouteLocationNormalized, NavigationGuardNext } from "vue-router";
import store from "@plugins/store";
import appConfigs from "@configs/app";
import { isDefined, isEmpty } from "@utils/var.check";
import objectUtils from "@utils/object.utils.ts";

export function isValidRedirectUrlDomain(url) {
  return (
    !isEmpty(url) && appConfigs.allowedRedirectDomains.find((domain) => url.startsWith(domain))
  );
}

// SLS-11785 Redirect old search urls to new search urls.
export function redirectOldToNewSearchUrls(to: RouteLocationNormalized, next: NavigationGuardNext) {
  // not an old search route name, no redirection needed.
  // to.redirectedFrom: prevents infinite loop due to query parameters cleanup that triggers redirection.
  if (to.path !== "/search" || to.redirectedFrom) {
    return false;
  }

  const isStudent = store.getters["auth/isStudent"];
  // an old search url, do redirection.
  // no query parameters, will redirect to find resource browse page.
  if (Object.keys(to.query).length === 0) {
    next({
      name: isStudent ? "FindMoeLibrary" : "FindResource",
      query: {
        redirect: true,
      },
    });
    return true;
  }

  // redirect to respective page with original query params (unused will be ignored).
  // if it has keyword -> respective location keyword search; no keyword -> find resource browse page.
  const newQuery = isDefined(to.query) ? objectUtils.deepClone(to.query) : {};

  const newRouteName = getRouteNameByLocation(newQuery, isStudent); // must be done before clean up query functions.
  if (!queryHasKeyword(newQuery)) {
    cleanUpQueryForBrowsePage(newQuery);
    next({
      name: newRouteName,
      query: { ...newQuery, redirect: true },
    });
    return true;
  }
  const corrected = cleanUpQueryForSearchPage(newQuery, newRouteName, isStudent);
  if (isStudent || corrected) {
    next({
      name: newRouteName,
      query: { ...newQuery, redirect: true },
    });
    return true;
  }

  return false;
}

// ── Private helpers ───────────────────────────────────────────────────────────

function queryHasKeyword(query: LocationQuery) {
  return query?.keyword && query?.keyword !== "";
}

function getRouteNameByLocation(query: LocationQuery, isStudent: boolean) {
  //when no location is specified
  const defaultRouteName = isStudent
    ? { search: "MoeLibrarySearch", browse: "FindMoeLibrary" }
    : { search: "ResourceSearch", browse: "FindResource" };

  if (!query?.location) {
    return queryHasKeyword(query) ? defaultRouteName.search : defaultRouteName.browse;
  }

  //when query is a string. force it into an array for uniform processing
  if (typeof query.location === "string") {
    query.location = [query.location];
  }

  if (query.location.includes("EXTERNAL_RESOURCES")) {
    return queryHasKeyword(query) ? "ExternalResourceSearch" : "ExternalResource";
  }
  if (query.location.includes("APP")) {
    return queryHasKeyword(query) ? "AppLibrarySearch" : "AppLibrary";
  }

  return queryHasKeyword(query) ? defaultRouteName.search : defaultRouteName.browse;
}

function cleanUpLocationQuery(query: LocationQuery) {
  if (!query?.location) {
    return false;
  }

  //when query is a string. force it into an array for uniform processing
  if (typeof query.location === "string") {
    query.location = [query.location];
  }

  const INVALID_LOCATION_VALUES = ["APP", "MINE", "EXTERNAL_RESOURCES"];
  if (!INVALID_LOCATION_VALUES.some((v) => query.location.includes(v))) {
    return false;
  }

  //replace query location with only valid locations
  query.location = query.location.filter((location) => !INVALID_LOCATION_VALUES.includes(location));
  return true;
}

function cleanUpQueryForBrowsePage(query: LocationQuery) {
  let hasCorrection = false;
  const BROWSE_PAGE_UNSUPPORTED_KEYS = ["location", "resource", "resourceType", "area"];
  for (const key of BROWSE_PAGE_UNSUPPORTED_KEYS) {
    if (query[key]) {
      delete query[key];
      hasCorrection = true;
    }
  }
  return hasCorrection;
}

function cleanUpQueryForSearchPage(query: LocationQuery, newRouteName: string, isStudent: boolean) {
  let hasCorrection = false;

  if ((newRouteName === "FindMoeLibrary" || newRouteName === "MoeLibrarySearch") && isStudent) {
    query.resource = "LESSON";
  } else if (query.resource === "APP" || query.resource === "WEBSITE") {
    delete query.resource;
    hasCorrection = true;
  } else if (query.resource === "RUBRIC") {
    hasCorrection = true;
    query.resource = "LESSON";
  }

  if (cleanUpLocationQuery(query)) {
    hasCorrection = true;
  }

  if (
    typeof query.resourceType === "string" &&
    query.resourceType.split(",").some((v) => /^\d+$/.test(v.trim()))
  ) {
    hasCorrection = true;
    delete query.resourceType;
  }

  const SEARCH_PAGE_UNSUPPORTED_KEYS = ["area", "syllabus", "parentTopic", "topic", "authors"];
  for (const key of SEARCH_PAGE_UNSUPPORTED_KEYS) {
    if (query[key]) {
      delete query[key];
      hasCorrection = true;
    }
  }

  return hasCorrection;
}
